import { useCallback, useMemo, useState } from "react";

import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields, CustomRoleAction } from "components/AssignRoleForm/types";
import { showPreviewPermissionsDrawer } from "components/PreviewPermissions";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import FormLoading from "components/form/components/loading";
import MissingDataBanner from "components/MissingDataBanner";
import Box from "ds/components/Box";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import useTypedContext from "hooks/useTypedContext";
import useStackRoleBindings from "shared/Stack/Roles/useStackRoleBindings";
import useStackRoleBindingBatchCreate from "shared/Stack/Roles/useStackRoleBindingBatchCreate";
import useStackRoleBindingDelete from "shared/Stack/Roles/useStackRoleBindingDelete";
import { CREATE_FORM_ID } from "components/AssignRoleForm/constants";
import FlashContext from "components/FlashMessages/FlashContext";
import { pluralize } from "shared/Pluralize";
import { getDocsUrl } from "utils/getDocsUrl";

import NewStackFooter from "../Footer";
import { StackFormContext } from "../context";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import { useNewStackAnalyticsSegmentEvent } from "../useNewStackAnalyticsSegmentEvent";
import Documentation from "./Documentation";

const refetchQueries = ["StackRoleBindingsWithRoles"];

const NewStackRoles = () => {
  const { createdStackId } = useTypedContext(StackFormContext);
  const { reportSuccess } = useTypedContext(FlashContext);
  const trackSegmentEvent = useNewStackAnalyticsSegmentEvent();
  const [isFormDirty, setIsFormDirty] = useState(false);

  useObserveForWarning(
    isFormDirty,
    <>
      You have <strong>unsaved changes</strong> in the role assignment form. Do you want to continue
      without saving them?
    </>
  );

  const setIsDirtyHandler = useCallback((formId: string, isDirty: boolean) => {
    if (formId === CREATE_FORM_ID) {
      setIsFormDirty(isDirty);
    }
  }, []);

  const {
    stackRoleBindings,
    roles: availableRoles,
    loading,
    hasData,
    error,
    refetch,
  } = useStackRoleBindings({
    stackID: createdStackId,
  });

  const assignedRoles = useMemo(
    () =>
      stackRoleBindings.map((binding) => ({
        id: binding.id,
        roleID: binding.role.id,
        spaceID: binding.space.id,
        spaceName: binding.space.name,
      })),
    [stackRoleBindings]
  );

  const [batchCreateStackRoleBinding, { loading: createBatchRoleBindingLoading }] =
    useStackRoleBindingBatchCreate({
      refetchQueries,
    });

  const onCreate = useCallback(
    (formData: AssignRoleFormAddFields) => {
      const roleBindings = formData.roleIds.map((roleId) => ({
        roleID: roleId,
        spaceID: formData.space,
        stackID: createdStackId,
      }));

      return batchCreateStackRoleBinding(
        {
          input: {
            bindings: roleBindings,
          },
        },
        (data) => {
          if (data?.stackRoleBindingBatchCreate?.length) {
            const count = data.stackRoleBindingBatchCreate.length;
            reportSuccess({
              message: `${pluralize("Role", count)} successfully assigned`,
            });
            trackSegmentEvent("Stack role binding added", { count });
          }
        }
      );
    },
    [batchCreateStackRoleBinding, createdStackId, reportSuccess, trackSegmentEvent]
  );

  const [deleteStackRoleBinding, { loading: deleteStackRoleBindingLoading }] =
    useStackRoleBindingDelete({
      refetchQueries,
    });

  const onDelete = useCallback(
    (roleAssignmentId: string) => {
      deleteStackRoleBinding(
        {
          id: roleAssignmentId,
        },
        (data) => {
          if (data?.stackRoleBindingDelete?.id) {
            reportSuccess({
              message: "Role assignment successfully removed",
            });
            trackSegmentEvent("Stack role binding removed");
          }
        }
      );
    },
    [deleteStackRoleBinding, reportSuccess, trackSegmentEvent]
  );

  const customActions: CustomRoleAction[] = useMemo(() => {
    return [
      {
        label: "Preview permissions",
        onAction: (roleAssignmentId: string) => {
          const binding = stackRoleBindings.find((b) => b.id === roleAssignmentId);
          if (binding) {
            showPreviewPermissionsDrawer({ roleId: binding.role.id });
            trackSegmentEvent("Preview permissions opened");
          }
        },
      },
    ];
  }, [stackRoleBindings, trackSegmentEvent]);

  const loadingContent = loading && !hasData;

  useErrorHandlerNewStack(error);

  if (loadingContent) {
    return <FormLoading />;
  }

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Assign roles"
          description="Assign roles to let this stack perform actions outside its own space."
        />

        <Box direction="column" gap="large">
          {!hasData && (
            <Box margin="large 0 0 0" direction="column">
              <MissingDataBanner
                text="Couldn't load roles. Please try to refresh or come back later."
                refreshHandler={refetch}
                refreshLoading={loading}
              />
            </Box>
          )}

          <AssignRoleForm
            onCreate={onCreate}
            setDirtyFields={setIsDirtyHandler}
            availableRoles={availableRoles}
            assignedRoles={assignedRoles}
            loading={loading}
            createLoading={createBatchRoleBindingLoading}
            onDelete={onDelete}
            customActions={customActions}
          />
        </Box>
      </FullScreenModalBody>
      <NewStackFooter
        loading={loading || deleteStackRoleBindingLoading || createBatchRoleBindingLoading}
        documentationLink={getDocsUrl("/concepts/stack/creating-a-stack#5-assign-roles")}
        documentationTitle="Stack roles"
        documentationBody={<Documentation />}
      />
    </>
  );
};

export default NewStackRoles;
